/* ---------- CP-001's -FC2 becomes the 24 V DC electronic circuit breaker (#2168, #2169; owner, 16 October) ----------
 * The library's electronic breaker has a third terminal, 0V, for its electronics, wired from -XD2:6 (lead, 6228053217). A save made
 * with the miniature breaker holds -FC2 with terminals 1 and 2 only, and a panel wired without that conductor, which the as-built
 * check would now call a missing connection. How such a save is read: the save migration in packages/progress uses this (format 18).
 * Self-contained on purpose, as cable.ts: the migration imports this file alone, not CP-001's model. */

/** -FC2's third terminal, as CP-001's DEFS name it, and the terminal its conductor comes from */
export const CP001_FC2_0V = { terminal: { id: "0V", name: "0 V for its electronics", type: "control" }, from: { tag: "XD2", terminalId: "6" } } as const;

/** A model ({ components, connections }) with -FC2's 0V terminal, and its conductor from -XD2:6 where the panel's -FC2 is already wired
 *  (a wire on its 1 or 2): the device changed under the learner's work, so its new conductor comes with it. Anything else (no -FC2,
 *  no -XD2, -FC2 not wired yet, already done) keeps its wires; the terminal is added wherever -FC2 is. */
function fc2Model(m: any): any {
  const comps: any[] = m?.components || [], f = comps.find(c => c?.tag === "FC2" && c?.type === "dc_breaker");
  if (!f) return m;
  const x = comps.find(c => c?.tag === CP001_FC2_0V.from.tag), wires: any[] = m.connections || [];
  const on = (c: any, t?: string) => (w: any) => [w?.from, w?.to].some(e => e?.componentId === c.id && (t === undefined || e?.terminalId === t));
  if (!(f.terminals || []).some((t: any) => t?.id === "0V")) f.terminals = [...(f.terminals || []), { ...CP001_FC2_0V.terminal }];
  if (x && wires.some(w => w?.conductorType !== "bridge" && (on(f, "1")(w) || on(f, "2")(w))) && !wires.some(on(f, "0V")))
    m.connections = [...wires, { id: `w${f.id}0v`, from: { componentId: x.id, terminalId: CP001_FC2_0V.from.terminalId }, to: { componentId: f.id, terminalId: "0V" },
      conductorType: "control", wireNumber: null }];
  return m;
}

const one = <A extends { model?: any }>(s: A): A => {
  if (!s || !s.model) return s;
  const text = typeof s.model === "string";
  let m: any; try { m = text ? JSON.parse(s.model) : JSON.parse(JSON.stringify(s.model)); } catch { return s; }
  const out = fc2Model(m);
  return { ...s, model: text ? JSON.stringify(out, null, 2) : out };
};
/** A saved CP-001 project ({ model, ui }, the model as a JSON string or an object, as saved) with -FC2 as the electronic breaker: its
 *  own work and each earlier attempt it keeps ("Start again", #1220). */
export function fc2Cp001Save<T extends { model?: any; attempts?: any[] }>(s: T): T {
  const out = one(s);
  return Array.isArray(s?.attempts) ? { ...out, attempts: s.attempts.map(a => one(a)) } : out;
}
